import { Puzzle } from "@/utils/util_types";
import { useLangDataStore } from "@/utils/util_states";

// multi_lang_data 的类型定义
type MultiLangData = {
    [langCode: string]: {
        soup?: string;
        title?: string;
        truth?: string;
        /** 关键线索点的多语言翻译，与 keypoints 一一对应 */
        keypoints?: string[];
    };
};

/**
 * 解析 keypoints：优先 multi_lang_data，其次原文，最后默认值
 * @param rawKeypoints 数据库中的原始 keypoints
 * @param multiLangData 多语言数据
 * @param languageCode 当前语言代码
 * @param defaultKeypoints 保底默认值（如 凶手/手法/动机 的多语言版）
 * @param maxCount 最大数量
 */
export function resolveKeypointsWithMultiLang(
    rawKeypoints: string[] | undefined | null,
    multiLangData: MultiLangData | undefined | null,
    languageCode: string,
    defaultKeypoints: string[],
    maxCount: number = 4
): string[] {
    // 1. 尝试从 multi_lang_data 获取当前语言的 keypoints
    if (multiLangData && typeof multiLangData === 'object') {
        const langPrefix = languageCode.split('-')[0];
        let langData = multiLangData[languageCode]
            ?? multiLangData[languageCode.toLowerCase()]
            ?? multiLangData[langPrefix]
            ?? multiLangData[langPrefix.toLowerCase()];

        if (langData?.keypoints && Array.isArray(langData.keypoints) && langData.keypoints.length > 0) {
            const kps = langData.keypoints.slice(0, maxCount);
            if (kps.length >= 1) return kps;
        }
    }

    // 2. 使用原文 keypoints
    if (Array.isArray(rawKeypoints) && rawKeypoints.length > 0) {
        const kps = rawKeypoints.slice(0, maxCount);
        if (kps.length >= 1) return kps;
    }

    // 3. 保底默认值
    return defaultKeypoints.slice(0, maxCount);
}

// 扩展的 Puzzle 类型，包含 multi_lang_data 字段
type PuzzleWithMultiLang = Puzzle & {
    multi_lang_data?: MultiLangData | null;
};

/**
 * 处理单个puzzle的多语言数据覆盖
 * 如果multi_lang_data中有当前语言的key（优先完整匹配，其次前缀匹配），
 * 按字段覆盖可用的多语言内容。公开页面下发的数据没有 truth，仍应能切换标题和汤面。
 */
export function applyMultiLangToPuzzle(puzzle: PuzzleWithMultiLang, languageCode: string): Puzzle {
    // 如果puzzle没有multi_lang_data，直接返回
    if (!puzzle.multi_lang_data || typeof puzzle.multi_lang_data !== 'object') {
        return puzzle as Puzzle;
    }

    // 获取语言代码的前两个字母（如 'zh-TW' -> 'zh'）
    const langPrefix = languageCode.split('-')[0];

    // 1. 先尝试用原始语言代码精确匹配（如 'ja-JP'）
    let langData = puzzle.multi_lang_data[languageCode];

    // 2. 如果失败，尝试用小写的完整代码匹配（如 'ja-jp'）
    if (!langData || typeof langData !== 'object') {
        const normalizedLangCode = languageCode.toLowerCase();
        langData = puzzle.multi_lang_data[normalizedLangCode];
    }

    // 3. 如果失败，尝试用原始前缀匹配（如 'ja'）
    if (!langData || typeof langData !== 'object') {
        langData = puzzle.multi_lang_data[langPrefix];
    }

    // 4. 如果失败，尝试用小写前缀匹配（如 'en'）
    if (!langData || typeof langData !== 'object') {
        const normalizedPrefix = langPrefix.toLowerCase();
        langData = puzzle.multi_lang_data[normalizedPrefix];
    }

    if (!langData || typeof langData !== 'object') {
        return puzzle as Puzzle;
    }

    // 每个字段独立覆盖；公开数据会主动移除 truth 和 keypoints。
    const hasSoup = langData.soup && typeof langData.soup === 'string' && langData.soup.trim() !== '';
    const hasTitle = langData.title && typeof langData.title === 'string' && langData.title.trim() !== '';
    const hasTruth = langData.truth && typeof langData.truth === 'string' && langData.truth.trim() !== '';

    if (hasSoup || hasTitle || hasTruth) {
        return {
            ...puzzle,
            ...(hasSoup ? { soup: langData.soup } : {}),
            ...(hasTitle ? { title: langData.title } : {}),
            ...(hasTruth ? { truth: langData.truth } : {}),
        } as Puzzle;
    }

    return puzzle as Puzzle;
}

/**
 * 批量处理puzzles数组的多语言数据覆盖
 */
export function applyMultiLangToPuzzles(puzzles: PuzzleWithMultiLang[], languageCode: string): Puzzle[] {
    return puzzles.map(puzzle => applyMultiLangToPuzzle(puzzle, languageCode));
}

/**
 * 获取puzzles数据并应用多语言覆盖
 * 这是一个封装函数，用于统一处理所有获取puzzles数据的场景
 */
export async function fetchPuzzlesWithMultiLang(
    queryFn: () => Promise<{ data: any[] | null; error: any }>,
    languageCode: string
): Promise<{ puzzles: Puzzle[]; error: any }> {
    try {
        const { data, error } = await queryFn();

        if (error) {
            return { puzzles: [], error };
        }

        if (!data || data.length === 0) {
            return { puzzles: [], error: null };
        }

        // 将数据转换为PuzzleWithMultiLang类型并应用多语言覆盖
        const puzzlesWithMultiLang = data as unknown as PuzzleWithMultiLang[];
        const puzzles = applyMultiLangToPuzzles(puzzlesWithMultiLang, languageCode);

        return { puzzles, error: null };
    } catch (error) {
        console.error('获取puzzles数据失败:', error);
        return { puzzles: [], error };
    }
}
